import Bottombar from "@/components/bottombar"
import Button from "@/components/button"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import Combobox from "@/components/ui/combobox"
import { v2DeptSchemaToFormSchema } from "@/packaging-v2/lib/form"
import useStore from "@/store"
import { PackagingV2Sheets, V2FormSchema } from "@/store/packaging-v2"
import { LabelValue } from "@/types/common"
import { useState, useMemo } from "react"

export interface AddDeptSheetProps {
  open: boolean
  onOpenChange?: (open: boolean) => void
}

/**
 * Sheet for adding more departments to the current shift
 */
export default function AddDeptSheet({ open, onOpenChange }: AddDeptSheetProps) {
  const packagingV2Store = useStore.use.packagingV2()
  const [selected, setSelected] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)

  // Get available departments (not already added)
  const availableDepts = useMemo(() => {
    const addedDeptIds = new Set(
      packagingV2Store.formSchemas.map((f) => f.id)
    )
    return packagingV2Store.allDepartmentsOptions.filter(
      (dept) => !addedDeptIds.has(dept.id!)
    )
  }, [packagingV2Store.allDepartmentsOptions, packagingV2Store.formSchemas])

  async function handleAddDepts() {
    if (selected.length === 0) return

    try {
      setIsLoading(true)

      const forms: V2FormSchema[] = []

      for (const deptId of selected) {
        const schema = packagingV2Store.getDeptSchema(deptId)
        if (!schema) continue

        const form = v2DeptSchemaToFormSchema(schema)
        forms.push(form)
      }

      packagingV2Store.addDeptForms(forms)

      // Set first new dept as current
      if (forms.length > 0) {
        packagingV2Store.setCurrDeptId(forms[0].id)
      }

      // Close sheet
      handleClose()
    } catch (err) {
      console.error("Error adding departments:", err)
    } finally {
      setIsLoading(false)
    }
  }

  function handleClose() {
    setSelected([])
    packagingV2Store.setCurrSheet(PackagingV2Sheets.None)
    onOpenChange?.(false)
  }

  function handleSelectDepts(value: string[]) {
    setSelected(value)
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-xl">
        <SheetHeader>
          <SheetTitle>Add Department</SheetTitle>
        </SheetHeader>

        <div className="px-4 pb-4">
          {availableDepts.length > 0 ? (
            <Combobox
              className="mt-4"
              label="Select Departments"
              placeholder="Select departments"
              options={availableDepts as LabelValue[]}
              maxAllowed={4}
              multiple
              onChange={handleSelectDepts}
            />
          ) : (
            <div className="text-center py-4">
              <p className="text-gray-500">
                All available departments have been added.
              </p>
            </div>
          )}
        </div>

        <Bottombar className="flex gap-4 bg-white">
          {availableDepts.length > 0 ? (
            <>
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                className="flex-1"
                disabled={selected.length === 0}
                isLoading={isLoading}
                onClick={handleAddDepts}
              >
                Add {selected.length > 0 ? `(${selected.length})` : ""}
              </Button>
            </>
          ) : (
            <Button className="flex-1" variant="outline" onClick={handleClose}>
              Close
            </Button>
          )}
        </Bottombar>
      </SheetContent>
    </Sheet>
  )
}
